<template>
  <BaseFetchData :action="fetchDuplicateContacts">
    <!-- props
      fetchData, status.loading, status.resolved, status.rejected, rejectReason
    -->
    <template>
      <div>
        <div>
          <BaseButton primary outline @click="$emit('change-contact', null)">
            Back to contacts
          </BaseButton>
          <h3 class="mt-4 text-lg font-medium">Select duplicates to merge</h3>
          <p class="text-sm mt-2 font-normal text-gray-600">
            Select one or more from the duplicate contacts to merge with the primary.
          </p>
        </div>
        <div class="mt-8 flex flex-col">
          <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table class="min-w-full divide-y divide-gray-300">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        class="py-3 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 sm:pl-6"
                      ></th>
                      <th
                        scope="col"
                        class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                      >
                        {{ $t('global.firstName') }}
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                      >
                        {{ $t('global.lastName') }}
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                      >
                        {{ $t('global.emails') }}
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                      >
                        {{ $t('global.phones') }}
                      </th>
                      <th
                        scope="col"
                        class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {{ $t('global.created') }}
                      </th>
                      <th scope="col" class="relative py-3 pl-3 pr-4 sm:pr-6">
                        <span class="sr-only">Edit</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200 bg-white">
                    <tr>
                      <td
                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6 bg-gray-100"
                      ></td>
                      <td
                        colspan="5"
                        class="whitespace-nowrap py-4 pl-3 pr-6 text-sm font-medium text-gray-900 sm:pr-6 bg-gray-100"
                      >
                        <h3 class="text-base font-medium">Primary</h3>
                      </td>
                    </tr>
                    <tr>
                      <td
                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6"
                      ></td>
                      <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        {{ contact.first_name }}
                      </td>
                      <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        {{ contact.last_name }}
                      </td>
                      <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        <ul>
                          <li
                            v-for="emailAddress in contact.email_addresses"
                            :key="emailAddress.id"
                          >
                            {{ emailAddress.address }}
                          </li>
                        </ul>
                      </td>
                      <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        <ul>
                          <li v-for="phoneNumber in contact.phone_numbers" :key="phoneNumber.id">
                            {{ phoneNumber.number }}
                          </li>
                        </ul>
                      </td>
                      <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        {{ getContactMeta(contact).short }}
                      </td>
                      <td
                        class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                      ></td>
                    </tr>
                    <tr class="bg-gray-100">
                      <th scope="col" class="relative w-12 px-6 sm:w-16 sm:px-8"></th>
                      <td
                        colspan="5"
                        class="whitespace-nowrap py-4 pl-3 pr-6 text-sm font-medium text-gray-900 sm:pr-6"
                      >
                        <div>
                          <h3 class="text-base font-medium">Possible duplicates</h3>

                          <div class="flex items-center space-x-3 mt-4">
                            <button
                              v-if="indeterminate || selectedDuplicateContactIds.length === 0"
                              type="button"
                              class="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-30"
                              @click="
                                changeSelectedDuplicateContactIds(
                                  duplicateContacts.map((p) => p.id)
                                )
                              "
                            >
                              Select all
                            </button>
                            <button
                              v-if="selectedDuplicateContactIds.length > 0"
                              type="button"
                              class="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-30"
                              @click="changeSelectedDuplicateContactIds([])"
                            >
                              Select none
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr
                      v-for="duplicateContact in duplicateContacts"
                      :key="duplicateContact.id"
                      :class="[
                        selectedDuplicateContactIds.includes(duplicateContact.id) && 'bg-gray-50',
                      ]"
                    >
                      <td class="relative w-12 px-6 sm:w-16 sm:px-8">
                        <div
                          v-if="selectedDuplicateContactIds.includes(duplicateContact.id)"
                          class="absolute inset-y-0 left-0 w-0.5 bg-indigo-600"
                        ></div>
                        <input
                          type="checkbox"
                          class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 sm:left-6"
                          :value="duplicateContact.id"
                          v-model="selectedDuplicateContactIdsInput"
                        />
                      </td>
                      <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        {{ duplicateContact.first_name }}
                      </td>
                      <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        {{ duplicateContact.last_name }}
                      </td>
                      <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        <ul>
                          <li
                            v-for="emailAddress in duplicateContact.email_addresses"
                            :key="emailAddress.id"
                          >
                            {{ emailAddress.address }}
                          </li>
                        </ul>
                      </td>
                      <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        <ul>
                          <li
                            v-for="phoneNumber in duplicateContact.phone_numbers"
                            :key="phoneNumber.id"
                          >
                            {{ phoneNumber.number }}
                          </li>
                        </ul>
                      </td>
                      <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        {{ getContactMeta(duplicateContact).short }}
                      </td>
                      <td
                        class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                      >
                        <a
                          role="button"
                          class="text-indigo-600 hover:text-indigo-900"
                          @click="$emit('change-contact', duplicateContact)"
                          >Make
                          <span class="sr-only">, {{ duplicateContact.full_name }}</span> primary</a
                        >
                      </td>
                    </tr>
                    <template v-if="selectedDuplicateContacts.length">
                      <tr class="bg-gray-100">
                        <th scope="col" class="relative w-12 px-6 sm:w-16 sm:px-8"></th>
                        <td
                          colspan="5"
                          class="whitespace-nowrap py-4 pl-3 pr-6 text-sm font-medium text-gray-900 sm:pr-6"
                        >
                          <div>
                            <h3 class="text-base font-medium">Merge rules</h3>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td
                          class="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6"
                        ></td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                          <BaseFormSelect
                            v-model="mergeRules.firstName"
                            :options="mergeRuleOptions.firstName"
                          />
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                          <BaseFormSelect
                            v-model="mergeRules.lastName"
                            :options="mergeRuleOptions.lastName"
                          />
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                          <BaseFormSelect
                            v-model="mergeRules.primaryEmailAddress"
                            :options="mergeRuleOptions.primaryEmailAddress"
                          />
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                          <BaseFormSelect
                            v-model="mergeRules.primaryPhoneNumber"
                            :options="mergeRuleOptions.primaryPhoneNumber"
                          />
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900"></td>
                        <td
                          class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                        >
                          <a
                            role="button"
                            class="text-indigo-600 hover:text-indigo-900"
                            @click="setDefaultMergeRules"
                            >Reset<span class="sr-only">, merge rules</span></a
                          >
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-8 flex justify-end">
          <BaseButton primary :disabled="selectedDuplicateContacts.length === 0" @click="submitForm"
            >Merge</BaseButton
          >
        </div>
      </div>
    </template>
  </BaseFetchData>
</template>

<script>
import moment from 'moment';
import { uniqBy, findIndex } from 'lodash-es';
import { contactDuplicatesClient } from '@/api/clients';
export default {
  props: {
    contact: {
      type: Object,
      required: true,
    },
    onMerge: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      duplicateContacts: [],
      selectedDuplicateContactIds: [],
      mergeRules: {
        firstName: this.contact.id,
        lastName: this.contact.id,
        primaryEmailAddress: this.contact.primary_email_address_id,
        primaryPhoneNumber: this.contact.primary_phone_number_id,
      },
    };
  },
  computed: {
    selectedDuplicateContactIdsInput: {
      get() {
        return this.selectedDuplicateContactIds;
      },
      set(ids) {
        this.changeSelectedDuplicateContactIds(ids);
      },
    },
    indeterminate() {
      return (
        this.selectedDuplicateContactIds.length > 0 &&
        this.selectedDuplicateContactIds.length < this.duplicateContacts.length
      );
    },
    selectedDuplicateContacts() {
      return this.selectedDuplicateContactIds
        .map((id) => this.duplicateContacts.find((c) => c.id === id))
        .filter(Boolean);
    },
    mergeRuleOptions() {
      const contactOptions = [this.contact, ...this.selectedDuplicateContacts];
      const allEmailAddresses = contactOptions.reduce((acc, contact) => {
        return [...acc, ...contact.email_addresses];
      }, []);
      const allPhoneNumbers = contactOptions.reduce((acc, contact) => {
        return [...acc, ...contact.phone_numbers];
      }, []);
      return {
        firstName: uniqBy(contactOptions, 'first_name').map((i) => ({
          value: i.id,
          label: i.first_name,
        })),
        lastName: uniqBy(contactOptions, 'last_name').map((i) => ({
          value: i.id,
          label: i.last_name,
        })),
        primaryEmailAddress: uniqBy(allEmailAddresses, 'id').map((i) => ({
          value: i.id,
          label: i.address,
        })),
        primaryPhoneNumber: uniqBy(allPhoneNumbers, 'id').map((i) => ({
          value: i.id,
          label: i.number,
        })),
      };
    },
  },
  methods: {
    setDefaultMergeRules() {
      this.mergeRules = {
        firstName: this.contact.id,
        lastName: this.contact.id,
        primaryEmailAddress: this.contact.primary_email_address_id,
        primaryPhoneNumber: this.contact.primary_phone_number_id,
      };
    },
    fetchDuplicateContacts() {
      return contactDuplicatesClient.list(this.contact.id).then((response) => {
        this.duplicateContacts = response.data;
        return Promise.resolve();
      });
    },
    getContactMeta(contact) {
      const createdLocal = moment.utc(contact.created_at).local();
      return {
        short: createdLocal.format('DD/MM/YYYY h:mm:ss a'),
        fromNow: createdLocal.fromNow(),
      };
    },
    changeSelectedDuplicateContactIds(newSelectedDuplicateContactIds) {
      this.selectedDuplicateContactIds = [...newSelectedDuplicateContactIds];

      if (findIndex(this.mergeRuleOptions.firstName, ['value', this.mergeRules.firstName]) === -1) {
        this.mergeRules.firstName = this.contact.id;
      }

      if (findIndex(this.mergeRuleOptions.lastName, ['value', this.mergeRules.lastName]) === -1) {
        this.mergeRules.lastName = this.contact.id;
      }

      if (
        this.mergeRules.primaryEmailAddress !== null &&
        findIndex(this.mergeRuleOptions.primaryEmailAddress, [
          'value',
          this.mergeRules.primaryEmailAddress,
        ]) === -1
      ) {
        this.mergeRules.primaryEmailAddress = this.contact.primary_email_address_id;
      }

      if (
        findIndex(this.mergeRuleOptions.primaryPhoneNumber, [
          'value',
          this.mergeRules.primaryPhoneNumber,
        ]) === -1
      ) {
        this.mergeRules.primaryPhoneNumber = this.contact.primary_phone_number_id;
      }
    },
    submitForm() {
      const formData = {
        contacts: this.selectedDuplicateContactIds,
        contact_changes: {
          ...(this.mergeRules.firstName !== this.contact.id
            ? {
                first_name: this.mergeRuleOptions.firstName.find(
                  (o) => o.value === this.mergeRules.firstName
                ).label,
              }
            : {}),
          ...(this.mergeRules.lastName !== this.contact.id
            ? {
                last_name: this.mergeRuleOptions.lastName.find(
                  (o) => o.value === this.mergeRules.lastName
                ).label,
              }
            : {}),
          ...(this.mergeRules.primaryEmailAddress !== this.contact.primary_email_address_id
            ? {
                primary_email_address_id:
                  this.mergeRules.primaryEmailAddress === null
                    ? null
                    : this.mergeRuleOptions.primaryEmailAddress.find(
                        (o) => o.value === this.mergeRules.primaryEmailAddress
                      )?.value || null,
              }
            : {}),
          ...(this.mergeRules.primaryPhoneNumber !== this.contact.primary_phone_number_id
            ? {
                primary_phone_number_id:
                  this.mergeRules.primaryPhoneNumber === null
                    ? null
                    : this.mergeRuleOptions.primaryPhoneNumber.find(
                        (o) => o.value === this.mergeRules.primaryPhoneNumber
                      )?.value || null,
              }
            : {}),
        },
      };

      this.onMerge(formData);
    },
  },
};
</script>
